import axios from 'axios';
import { getValidAdminToken } from '../auth/authManager';
import { getEnvironmentConfig } from '../config/apiConfig';
import { getSelectedEndpointUrl } from '../endpoints/endpointStore';

// Helper to get admin API key safely
const getAdminApiKey = () => {
  const config = getEnvironmentConfig();
  return config?.apiConfig?.ADMIN_API_KEY || '';
};

// Helper to get config service base URL from cache (already fetched during login)
const getConfigServiceUrl = async () => {
  try {
    // Get the cached endpoint from localStorage (fetched during login)
    const cachedEndpoint = getSelectedEndpointUrl();
    
    if (cachedEndpoint) {
      console.log('[ServiceAPI] Using cached config service endpoint:', cachedEndpoint);
      return cachedEndpoint;
    }
    
    throw new Error('No config service endpoint cached. Please ensure login completed successfully.');
  } catch (error) {
    console.error('[ServiceAPI] Failed to get config service URL from cache:', error);
    throw error;
  }
};

// Helper to make requests to config service using /api/admin prefix
const configServiceRequest = async (url, options = {}) => {
  const token = getValidAdminToken();
  
  if (!token) {
    throw new Error('No admin token found. Please authenticate first.');
  }
  
  const configUrl = await getConfigServiceUrl();
  const apiUrl = url.startsWith('/') ? url : `/${url}`;
  // Ensure protocol is included (https://)
  const baseUrl = configUrl.startsWith('http') ? configUrl : `https://${configUrl}`;
  const fullUrl = `${baseUrl}/admin-api/v1${apiUrl}`;
  
  console.log('[ServiceAPI] Config service request:', {
    url: apiUrl,
    fullUrl,
    method: options.method || 'GET',
    token: token ? `${token.substring(0, 20)}...` : 'missing',
    apiKey: getAdminApiKey() ? 'present' : 'missing'
  });
  
  try {
    const response = await axios.request({
      url: fullUrl,
      method: options.method || 'GET',
      data: options.data,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': getAdminApiKey(),
        ...options.headers
      },
      timeout: 10000,
      ...options
    });
    
    return response;
  } catch (error) {
    console.error('[ServiceAPI] Config service request failed:', {
      url,
      fullUrl,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });
    throw error;
  }
};

export const serviceAPI = {
  // Trade Endpoints Management
  getAll: async () => {
    console.log('Calling GET /trade-endpoints via config service');
    try {
      const response = await configServiceRequest('/trade-endpoints');
      console.log('GET /trade-endpoints response:', response);
      return response;
    } catch (error) {
      console.error('GET /trade-endpoints error:', error);
      throw error;
    }
  },

  getStatus: async () => {
    console.log('Calling GET /trade-endpoints/status via config service');
    try {
      const response = await configServiceRequest('/trade-endpoints/status');
      console.log('GET /trade-endpoints/status response:', response);
      return response;
    } catch (error) {
      console.error('GET /trade-endpoints/status error:', error);
      throw error;
    }
  },

  create: async (data) => {
    console.log('Calling POST /trade-endpoints with data:', data);
    try {
      const response = await configServiceRequest('/trade-endpoints', {
        method: 'POST',
        data: {
          endpoint: data.name,
          name: `Trade Engine ${data.name}`,
          description: data.description || `Trading endpoint for automated trading system`
        }
      });
      console.log('POST /trade-endpoints response:', response);
      return response;
    } catch (error) {
      console.error('POST /trade-endpoints error:', error);
      throw error;
    }
  },

  update: async (endpointName, data) => {
    console.log(`Calling PUT /trade-endpoints/${endpointName} with data:`, data);
    try {
      const response = await configServiceRequest(`/trade-endpoints/${endpointName}`, {
        method: 'PUT',
        data: {
          endpoint: data.name,
          name: `Trade Engine ${data.name}`,
          description: data.description
        }
      });
      console.log(`PUT /trade-endpoints/${endpointName} response:`, response);
      return response;
    } catch (error) {
      console.error(`PUT /trade-endpoints/${endpointName} error:`, error);
      throw error;
    }
  },

  delete: async (endpointName) => {
    console.log(`Calling DELETE /trade-endpoints/${endpointName}`);
    try {
      const response = await configServiceRequest(`/trade-endpoints/${endpointName}`, {
        method: 'DELETE'
      });
      console.log(`DELETE /trade-endpoints/${endpointName} response:`, response);
      return response;
    } catch (error) {
      console.error(`DELETE /trade-endpoints/${endpointName} error:`, error);
      throw error;
    }
  },

  scale: async (endpointName, replicas) => {
    console.log(`Calling POST /trade-endpoints/${endpointName}/scale with replicas:`, replicas);
    try {
      const response = await configServiceRequest(`/trade-endpoints/${endpointName}/scale`, {
        method: 'POST',
        data: { replicas }
      });
      console.log(`POST /trade-endpoints/${endpointName}/scale response:`, response);
      return response;
    } catch (error) {
      console.error(`POST /trade-endpoints/${endpointName}/scale error:`, error);
      throw error;
    }
  },

  // Quote Endpoints Management
  getAllQuotes: async () => {
    console.log('Calling GET /quote-endpoints via config service');
    try {
      const response = await configServiceRequest('/quote-endpoints');
      console.log('GET /quote-endpoints response:', response);
      return response;
    } catch (error) {
      console.error('GET /quote-endpoints error:', error);
      throw error;
    }
  },

  getQuoteStatus: async () => {
    console.log('Calling GET /quote-endpoints/status via config service');
    try {
      const response = await configServiceRequest('/quote-endpoints/status');
      console.log('GET /quote-endpoints/status response:', response);
      return response;
    } catch (error) {
      console.error('GET /quote-endpoints/status error:', error);
      throw error;
    }
  },

  createQuote: async (data) => {
    console.log('Calling POST /quote-endpoints with data:', data);
    try {
      const response = await configServiceRequest('/quote-endpoints', {
        method: 'POST',
        data: {
          host: data.ip,
          port: data.port
        }
      });
      console.log('POST /quote-endpoints response:', response);
      return response;
    } catch (error) {
      console.error('POST /quote-endpoints error:', error);
      throw error;
    }
  },

  deleteQuote: async (endpoint) => {
    console.log(`Calling DELETE /quote-endpoints/${endpoint}`);
    try {
      const response = await configServiceRequest(`/quote-endpoints/${endpoint}`, {
        method: 'DELETE'
      });
      console.log(`DELETE /quote-endpoints/${endpoint} response:`, response);
      return response;
    } catch (error) {
      console.error(`DELETE /quote-endpoints/${endpoint} error:`, error);
      throw error;
    }
  },

  // Public Interface Management
  getPublicInterfaceStatus: async () => {
    console.log('Calling GET /instance/primary-status via config service');
    try {
      const response = await configServiceRequest('/instance/primary-status');
      console.log('GET /instance/primary-status response:', response);
      return response;
    } catch (error) {
      console.error('GET /instance/primary-status error:', error);
      throw error;
    }
  },

  updatePublicInterfacePrimary: async (url, isPrimary) => {
    console.log(`Calling PUT /instance/primary-status with isPrimary:`, isPrimary);
    try {
      const response = await configServiceRequest('/instance/primary-status', {
        method: 'PUT',
        data: { isPrimary }
      });
      console.log('PUT /instance/primary-status response:', response);
      return response;
    } catch (error) {
      console.error('PUT /instance/primary-status error:', error);
      throw error;
    }
  }
};
